import { apiFetch } from './apiClient';

export const SETTING_KEYS = ['neuralSynthesis', 'mijia', 'runtimeLog', 'calendar', 'learningFlow', 'wearable', 'carControl'] as const;
export type SettingKey = typeof SETTING_KEYS[number];

const DEFAULTS: Record<SettingKey, boolean> = {
  neuralSynthesis: false,
  mijia: false,
  runtimeLog: false,
  calendar: false,
  learningFlow: false,
  wearable: false,
  carControl: false,
};

// storageKey(本地 localStorage) <-> 设置键 双向映射
export const SETTING_TO_STORAGE: Record<SettingKey, string> = {
  neuralSynthesis: 'xf_neural_synthesis_enabled',
  mijia: 'xf_mijia_enabled',
  runtimeLog: 'xf_runtime_log_enabled',
  calendar: 'xf_calendar_enabled',
  learningFlow: 'xf_learning_flow_enabled',
  wearable: 'xf_wearable_enabled',
  carControl: 'xf_carcontrol_enabled',
};

export const STORAGE_TO_SETTING: Record<string, SettingKey> = Object.fromEntries(
  Object.entries(SETTING_TO_STORAGE).map(([k, v]) => [v, k as SettingKey]),
) as Record<string, SettingKey>;

// 缓存（进程内），以本地 localStorage 作为离线降级
const cache: Record<SettingKey, boolean> = { ...DEFAULTS };

function safeParse(v: any): Partial<Record<SettingKey, boolean>> {
  try { return (v && JSON.parse(v)) || {}; } catch { return {}; }
}

/**
 * 拉取服务端设置并水合：
 * - 首启迁移：服务端无记录时，用本地 localStorage 现有值播种上云
 * - 以服务端值为准，写回本地并派发 xf:setting-changed，让 DesktopUI 等实时同步
 */
export async function fetchUserSettings(): Promise<Record<SettingKey, boolean>> {
  try {
    const res = await apiFetch('/api/user/settings');
    if (!res.ok) return cache;
    const data = await res.json();
    const s: Partial<Record<SettingKey, boolean>> = data.settings || {};
    const stored: boolean = !!data.stored;

    // 首启迁移：用本地 localStorage 播种（仅当服务端尚无记录）
    const localSeed: Partial<Record<SettingKey, boolean>> = {};
    let hasLocal = false;
    for (const k of SETTING_KEYS) {
      const ls = localStorage.getItem(SETTING_TO_STORAGE[k]);
      if (ls !== null) { localSeed[k] = ls === 'true'; hasLocal = true; }
    }
    if (!stored && hasLocal) {
      await pushSettings(localSeed);
    }

    // 服务端为准，写回本地并派发事件
    for (const k of SETTING_KEYS) {
      if (k in s) {
        cache[k] = !!s[k];
        localStorage.setItem(SETTING_TO_STORAGE[k], String(!!s[k]));
        window.dispatchEvent(new CustomEvent('xf:setting-changed', {
          detail: { key: SETTING_TO_STORAGE[k], value: !!s[k] },
        }));
      }
    }
  } catch (e) {
    console.warn('[userSettings] 拉取失败，沿用本地缓存', e);
  }
  return cache;
}

/** 写入单个设置：更新缓存 + 本地 + 派发事件 + 上云 */
export async function setUserSetting(key: SettingKey, value: boolean): Promise<void> {
  cache[key] = value;
  const storageKey = SETTING_TO_STORAGE[key];
  localStorage.setItem(storageKey, String(value));
  window.dispatchEvent(new CustomEvent('xf:setting-changed', {
    detail: { key: storageKey, value },
  }));
  const patch: Partial<Record<SettingKey, boolean>> = {};
  patch[key] = value;
  await pushSettings(patch);
}

async function pushSettings(patch: Partial<Record<SettingKey, boolean>>): Promise<void> {
  try {
    await apiFetch('/api/user/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings: patch }),
    });
  } catch (e) {
    console.warn('[userSettings] 写入失败（本地已保存，下次同步）', e);
  }
}
